// GLOBALS
import React from 'react';
// import { useLocalSearchParams, useRouter } from "expo-router";
import * as Sentry from '@sentry/browser';

import { useAuth } from '../../contexts/AuthContext';
// FORM ELEMENTS
import { useForm } from '@tanstack/react-form';
import { FormSelect } from './FormSelect';

//CHART HANDLING
import useChart from '../../hooks/useChart';
import { Chart, PatientDoc, PaymentPathFlags, PAYMENT_PATH_NAV_SCREENS } from '@usacsmideo/types';
import { useUpdateChartSection } from '../../hooks/useUpdateChartSection';
import { notifyChartSaveError } from '../../utilities/chartSaveError';

// PATIENT DOCUMENT HANDLING
import usePatientDoc from '../../hooks/usePatientDoc';
import { useUpdatePaymentPathNav } from '../../hooks/useUpdatePaymentPathNav';

// DISPLAY ELEMENTS
// import { Text, Switch, View, Alert } from 'react-native';
// import { GreenActionButton } from '../buttons/GreenActionButton';
// import { styles } from '../../style/globalStyleSheet';
import { STATES_ALL, STATES_PREPAID } from './data/states';
import Checkbox from './Checkbox';
import { classNames } from '../../style/classNames';
import { Heading } from '../Heading';

interface PaymentPrefsProps {
  onComplete?: () => void;
}

export default function PaymentPrefs({ onComplete }: PaymentPrefsProps) {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const { mutate: mutateChart } = useUpdateChartSection();
  const patientDoc = usePatientDoc(patientId);
  const patientInfo = patientDoc?.data as PatientDoc;
  const { mutate: mutatePaymentPathNav } = useUpdatePaymentPathNav();
  const [stateOfResidence, setStateOfResidence] = React.useState<string>(chart?.PATIENT_INFO_2?.state || '');
  const [resetPending, setResetPending] = React.useState(false);

  // FILL THE STATE OF RESIDENCE FROM THE CHART WHEN IT ARRIVES AFTER MOUNT (e.g. Onboarding Payment after a
  // hard reload): the useState default above only sees the first render, which left "Select a state" and
  // no checkboxes (CU-17tm466h657 E2E W2). Only an EMPTY picker is filled, so a patient's choice stands.
  // Same as the app's PaymentPrefs.
  React.useEffect(() => {
    if (!stateOfResidence && chart?.PATIENT_INFO_2?.state) {
      setStateOfResidence(chart.PATIENT_INFO_2.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RUNS WHEN THE CHART'S STATE ARRIVES; LISTING stateOfResidence WOULD REFILL A PICKER THE PATIENT DELIBERATELY CLEARED
  }, [chart?.PATIENT_INFO_2?.state]);
  const [showNextButton, setShowNextButton] = React.useState(false);

  // REACTIVE RESET: when navType.base changes, reset the payment PATH flags and write them to Firestore.
  // ⚠️ CU-17tm466h657 — THIS NEVER WRITES alreadyPaid OR amountPaid. Payment status is recorded by the
  // server (recordStripePayment) or the AOB / $0 paths; a type change wrote a PAID patient back to
  // unpaid, and an unpaid chart offers Pay Now again. A PAID patient gets no reset at all.
  // THE BASELINE IS THE TYPE HELD WHEN BOTH LISTENERS HAVE RESOLVED (IT MAY BE UNDEFINED: NO CHOICE YET),
  // NOT THE FIRST DEFINED VALUE. A doc arriving after mount is not a type change, but a patient going
  // from no type to a type is, and still resets.
  const listenersResolved = patientDoc?.hasResolved === true && chartQuery?.hasResolved === true;
  const isPaid = chart?.PAYMENT_INFO?.alreadyPaid === true;
  const navTypeBaselineRef = React.useRef<{ base: string | undefined } | null>(null);
  React.useEffect(() => {
    if (!listenersResolved) return;
    const currentBase = patientInfo?.navType?.base;
    if (navTypeBaselineRef.current === null) {
      navTypeBaselineRef.current = { base: currentBase };
      return;
    }
    if (!currentBase) return;
    if (navTypeBaselineRef.current.base === currentBase) return;
    navTypeBaselineRef.current = { base: currentBase };
    if (isPaid) return;

    const resetValues: PaymentPathFlags =
      currentBase === 'self'
        ? {
            isPayingSelf: true,
            isPayingInsurance: false,
            isPayingMedicare: false,
            isPrePaidInsurance: false,
            isPrePaidMedicare: false,
          }
        : {
            isPayingSelf: false,
            isPayingInsurance: false,
            isPayingMedicare: false,
            isPrePaidInsurance: false,
            isPrePaidMedicare: false,
          };

    // Reset form state
    for (const [key, value] of Object.entries(resetValues)) {
      paymentInfoForm.setFieldValue(key as keyof PaymentPathFlags, value);
    }

    // Immediately write to Firestore
    mutateChart(
      { patientId, docData: { PAYMENT_INFO: resetValues } },
      {
        onSuccess: () => {
          setResetPending(true);
          setShowNextButton(false);
        },
        onError: (error) => {
          Sentry.captureException(error);
          console.error('Error resetting payment info:', error);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIRES ON A TYPE CHANGE (OR ON RESOLVING) ONLY; isPaid IS READ AT THAT MOMENT. LISTING IT WOULD ONLY RE-RUN THE EFFECT, WHICH THE BASELINE CHECK THEN STOPS
  }, [listenersResolved, patientInfo?.navType?.base]);

  // PAYMENT INFO FORM
  const paymentInfoForm = useForm({
    defaultValues: {
      isPayingInsurance: chart?.PAYMENT_INFO?.isPayingInsurance || false,
      isPayingMedicare: chart?.PAYMENT_INFO?.isPayingMedicare || false,
      isPayingSelf: chart?.PAYMENT_INFO?.isPayingSelf || false,
      isPrePaidInsurance: chart?.PAYMENT_INFO?.isPrePaidInsurance || false,
      isPrePaidMedicare: chart?.PAYMENT_INFO?.isPrePaidMedicare || false,
    },
  });

  // CHECK IF PAYMENT SELECTION MEETS OUR CRITERIA
  const isValidPaymentChoice = (update: { field: string; value: boolean }) => {
    const { field, value } = update;

    // MIDEO SELF-GUIDED
    if (patientInfo?.navType?.base === 'self') {
      if (field === 'isPayingSelf' && value === false) {
        alert('MIDEO Self-Guided patients can only pay with a valid credit card or HSA/FSA card.');
        return false;
      }
      if (field === 'isPayingSelf' && value === true) {
        return true; // ONLY VALID CHOICE
      }
      if (field === 'isPayingInsurance') {
        alert(
          'You may wish to consider the MIDEO Physician Guided Package if you would like to bill your insurance for potential reimbursement. ',
        );
        return false;
      }
      if (field === 'isPayingMedicare') {
        alert(
          'You may wish to consider the MIDEO Physician Guided Package if you would like to bill Medicare for potential reimbursement.',
        );
        return false;
      }
    }
    // MIDEO CONCIERGE
    if (patientInfo?.navType?.base === 'concierge') {
      // TURNING OFF SELF-PAYMENT
      if (field === 'isPayingSelf' && value === false && !STATES_PREPAID.includes(stateOfResidence)) {
        return true; // VALID CHOICE
      }
      if (field === 'isPayingSelf' && value === false && STATES_PREPAID.includes(stateOfResidence)) {
        alert('Prepayment is required in your state. You can still seek reimbursement from your provider.');
        return false;
      }
      // TURNING ON SELF-PAYMENT
      if (field === 'isPayingSelf') {
        if (value === true && !STATES_PREPAID.includes(stateOfResidence)) {
          alert(
            'While you are welcome to pay for MIDEO Concierge out of pocket, we are licensed to bill insurance in your state.',
          );
          return true; // TECHNICALLY A VALID CHOICE
        }
      }
      // INSURANCE PAYMENT SELECTED
      if (field === 'isPayingInsurance') {
        if (value === true && !STATES_PREPAID.includes(stateOfResidence)) {
          return true;
        }
        // MUST BE PRE-PAID
        if (value === true && STATES_PREPAID.includes(stateOfResidence)) {
          alert(
            'You will be billed for MIDEO Concierge out of pocket, but you may submit a claim to your insurance company for reimbursement.',
          );
          paymentInfoForm.setFieldValue('isPayingSelf', true); // TURN THIS ON TO MAKE PATIENT AWARE A CARD IS NEEDED
          paymentInfoForm.setFieldValue('isPrePaidInsurance', true);
          return true;
        }
      }
      // TURNING OFF INSURANCE PAYMENT
      if (field === 'isPayingInsurance' && value === false) {
        paymentInfoForm.setFieldValue('isPrePaidInsurance', false);
        return true;
      }
      // MEDICARE PAYMENT SELECTED
      if (field === 'isPayingMedicare') {
        if (value === true && !STATES_PREPAID.includes(stateOfResidence)) {
          return true;
        }
        // MUST BE PRE-PAID
        if (value === true && STATES_PREPAID.includes(stateOfResidence)) {
          alert(
            'You will be billed for MIDEO Concierge out of pocket, but you may submit a claim to Medicare for reimbursement.',
          );
          paymentInfoForm.setFieldValue('isPrePaidMedicare', true);
          return true;
        }
      }
      // TURNING OFF MEDICARE PAYMENT
      if (field === 'isPayingMedicare' && value === false) {
        paymentInfoForm.setFieldValue('isPrePaidMedicare', false);
        return true;
      }
    }
  };

  // PAYMENT INFO UPDATE FUNCTIONS - CALLED IN SUCCESSION
  const updatePaymentInfo = () => {
    mutateChart(
      {
        patientId: patientId,
        docData: {
          // PATH FLAGS ONLY: PaymentPrefs never writes payment status (CU-17tm466h657)
          PAYMENT_INFO: {
            isPayingInsurance: paymentInfoForm.state.values.isPayingInsurance,
            isPayingMedicare: paymentInfoForm.state.values.isPayingMedicare,
            isPayingSelf: paymentInfoForm.state.values.isPayingSelf,
            isPrePaidInsurance:
              paymentInfoForm.state.values.isPayingSelf && paymentInfoForm.state.values.isPayingInsurance,
            isPrePaidMedicare:
              paymentInfoForm.state.values.isPayingSelf && paymentInfoForm.state.values.isPayingMedicare,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          // ONLY A CONFIRMED SAVE CLEARS THE PENDING RESET AND REVEALS NEXT; ON FAILURE THE
          // UPDATE BUTTON STAYS SO THE PATIENT CAN RETRY
          setResetPending(false);
          if (onComplete) {
            setShowNextButton(true);
          }
          // UPDATE NAV OBJ
          updatePatientNavObjByPayment();
        },
        onError: () => {
          notifyChartSaveError();
        },
      },
    );
  };
  const updatePatientNavObjByPayment = () => {
    // ⚠️ CU-17tm466h657 — ONLY THE SIX PAYMENT SCREENS, AS FIELD PATHS, FROM THE SHARED RULE IN mideo-types
    // (getPaymentPathNavPatch). This used to spread the CACHED navObj, mutate it in place (the spread was
    // shallow) and write the WHOLE map back, reverting fields other writers had changed since.
    // A field-path write to a missing navObj, OR TO A MISSING SCREEN, would CREATE a partial, malformed
    // entry ({ disabled } with no id/path) that isWellFormedNavObj (key presence only) cannot see. The
    // layout's navObj recovery owns a missing navObj, so reaching here without one, or without one of the
    // six screens, is a bug: report it and write nothing. Same per-screen check as the console's
    // updateProductPayment (CU-17tm466h64w). (No throw: this runs in the save's onSuccess AFTER Next is
    // shown, so a throw would only add an unhandled error on top of the capture.)
    const storedNavObj = patientInfo?.navObj as unknown as Record<string, unknown> | undefined;
    const missingScreen = storedNavObj
      ? PAYMENT_PATH_NAV_SCREENS.find((screen) => !storedNavObj[screen] || typeof storedNavObj[screen] !== 'object')
      : undefined;
    if (!storedNavObj || missingScreen) {
      Sentry.captureException(new Error('PaymentPrefs.updatePatientNavObjByPayment: missing navObj'), {
        extra: { patientId, ...(missingScreen ? { missingScreen } : {}) },
      });
      return;
    }
    mutatePaymentPathNav(
      {
        patientId: patientId,
        flags: {
          isPayingSelf: paymentInfoForm.state.values.isPayingSelf,
          isPayingInsurance: paymentInfoForm.state.values.isPayingInsurance,
          isPayingMedicare: paymentInfoForm.state.values.isPayingMedicare,
          isPrePaidInsurance: paymentInfoForm.state.values.isPrePaidInsurance,
          isPrePaidMedicare: paymentInfoForm.state.values.isPrePaidMedicare,
        },
      },
      {
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  return (
    <div>
      <Heading id="product-selection-page-header" textContent="Payment Preferences" />
      <div>
        <FormSelect
          id="state-of-residence-picker"
          label="State of primary residence"
          name="stateOfResidence"
          onBlur={() => {}}
          onChange={(e) => setStateOfResidence(e.target.value ? e.target.value : '')}
          options={STATES_ALL}
          required={true}
          value={stateOfResidence}
        />
      </div>
      {stateOfResidence !== '' && (
        <div>
          {/* SELF-PAYMENT SWITCH */}
          <paymentInfoForm.Field name="isPayingSelf">
            {(field) => (
              <div
                style={{
                  flexDirection: 'row',
                  gap: 10,
                }}
              >
                <Checkbox
                  id="is-paying-self-checkbox"
                  label={`I plan on paying for my MIDEO with a credit card or HSA/FSA. ${
                    (patientInfo?.navType?.base === 'concierge' && STATES_PREPAID.includes(stateOfResidence)) ||
                    stateOfResidence === ''
                      ? 'I will then seek reimbursement from my insurance/Medicare'
                      : ''
                  }`}
                  name="isPayingSelf"
                  checked={field.state.value === true}
                  onChange={(e) => {
                    if (
                      isValidPaymentChoice({
                        field: 'isPayingSelf',
                        value: e.target.checked ? true : false,
                      }) === true
                    ) {
                      field.handleChange(e.target.checked ? true : false);
                    } else {
                      // INVALID CHOICE, DO NOT UPDATE
                    }
                  }}
                />
                {/*  */}
              </div>
            )}
          </paymentInfoForm.Field>
          {/* INSURANCE PAYMENT SWITCH */}
          <paymentInfoForm.Field name="isPayingInsurance">
            {(field) => (
              <div
                style={{
                  flexDirection: 'row',
                  gap: 10,
                }}
              >
                <Checkbox
                  id="is-paying-insurance-checkbox"
                  label="I plan on paying for my MIDEO with private insurance."
                  name="isPayingInsurance"
                  checked={field.state.value === true}
                  onChange={(e) => {
                    if (
                      isValidPaymentChoice({
                        field: 'isPayingInsurance',
                        value: e.target.checked ? true : false,
                      }) === true
                    ) {
                      field.handleChange(e.target.checked ? true : false);
                    } else {
                      // INVALID CHOICE, DO NOT UPDATE
                    }
                  }}
                />
              </div>
            )}
          </paymentInfoForm.Field>
          {/* MEDICARE SWITCH */}
          <paymentInfoForm.Field name="isPayingMedicare">
            {(field) => (
              <div
                style={{
                  flexDirection: 'row',
                  gap: 10,
                }}
              >
                <Checkbox
                  id="is-paying-medicare-checkbox"
                  label="I plan on paying for my MIDEO with Medicare."
                  name="isPayingMedicare"
                  checked={field.state.value === true}
                  onChange={(e) => {
                    if (
                      isValidPaymentChoice({
                        field: 'isPayingMedicare',
                        value: e.target.checked ? true : false,
                      }) === true
                    ) {
                      field.handleChange(e.target.checked ? true : false);
                    } else {
                      // INVALID CHOICE, DO NOT UPDATE
                    }
                  }}
                />
              </div>
            )}
          </paymentInfoForm.Field>
          {/* SUBMIT PAYMENT UPDATES BUTTON OR WARNING MESSAGE */}
          <paymentInfoForm.Subscribe
            selector={(state) => [state.values]}
            children={(values) => {
              const valuesObj = values[0];
              // SHOW WARNING IF NO PAYMENT METHOD SELECTED
              if (
                valuesObj.isPayingSelf === false &&
                valuesObj.isPayingInsurance === false &&
                valuesObj.isPayingMedicare === false &&
                valuesObj.isPrePaidInsurance === false &&
                valuesObj.isPrePaidMedicare === false
              ) {
                return (
                  <div style={{ padding: 20 }}>
                    <p
                      style={{
                        fontSize: 22,
                        color: 'red',
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}
                    >
                      Please select a payment method to continue.
                    </p>
                  </div>
                );
              }
              // SHOW UPDATE BUTTON IF PAYMENT INFO HAS CHANGED OR RESET IS PENDING
              if (
                resetPending ||
                valuesObj.isPayingSelf !== chart?.PAYMENT_INFO?.isPayingSelf ||
                valuesObj.isPayingInsurance !== chart?.PAYMENT_INFO?.isPayingInsurance ||
                valuesObj.isPayingMedicare !== chart?.PAYMENT_INFO?.isPayingMedicare ||
                valuesObj.isPrePaidInsurance !== chart?.PAYMENT_INFO?.isPrePaidInsurance ||
                valuesObj.isPrePaidMedicare !== chart?.PAYMENT_INFO?.isPrePaidMedicare
              )
                return (
                  <button
                    id="update-payment-info"
                    className={classNames.nextButton}
                    onClick={() => {
                      updatePaymentInfo();
                    }}
                  >
                    Update Payment Info
                  </button>
                );
            }}
          />
          {/* NEXT BUTTON (onboarding flow only) */}
          {showNextButton && onComplete && (
            <button id="submit-form-info" className={classNames.nextButton} onClick={onComplete}>
              Submit and Continue
            </button>
          )}
        </div>
      )}
    </div>
  );
}
